import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet'
import AllInboxIcon from '@mui/icons-material/AllInbox'
import ArticleIcon from '@mui/icons-material/Article'
import AssessmentIcon from '@mui/icons-material/Assessment'
import AssignmentReturnIcon from '@mui/icons-material/AssignmentReturn'
import AttachMoneyIcon from '@mui/icons-material/AttachMoney'
import AutoAwesomeMotionIcon from '@mui/icons-material/AutoAwesomeMotion'
import BuildIcon from '@mui/icons-material/Build'
import CellTowerIcon from '@mui/icons-material/CellTower'
import CurrencyExchangeIcon from '@mui/icons-material/CurrencyExchange'
import DashboardIcon from '@mui/icons-material/Dashboard'
import DescriptionIcon from '@mui/icons-material/Description'
import EventNoteIcon from '@mui/icons-material/EventNote'
import FactCheckIcon from '@mui/icons-material/FactCheck'
import FindInPageIcon from '@mui/icons-material/FindInPage'
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered'
import GroupsIcon from '@mui/icons-material/Groups'
import HistoryEduIcon from '@mui/icons-material/HistoryEdu'
import IntegrationInstructionsIcon from '@mui/icons-material/IntegrationInstructions'
import LayersIcon from '@mui/icons-material/Layers'
import LocalShippingIcon from '@mui/icons-material/LocalShipping'
import MailIcon from '@mui/icons-material/Mail'
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts'
import MapIcon from '@mui/icons-material/Map'
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn'
import MoneyOffIcon from '@mui/icons-material/MoneyOff'
import NotificationsIcon from '@mui/icons-material/Notifications'
import PaidIcon from '@mui/icons-material/Paid'
import PaymentIcon from '@mui/icons-material/Payment'
import PendingActionsIcon from '@mui/icons-material/PendingActions'
import PersonIcon from '@mui/icons-material/Person'
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong'
import RunningWithErrorsIcon from '@mui/icons-material/RunningWithErrors'
import SavingsIcon from '@mui/icons-material/Savings'
import SecurityIcon from '@mui/icons-material/Security'
import SettingsIcon from '@mui/icons-material/Settings'
import SettingsApplicationsIcon from '@mui/icons-material/SettingsApplications'
import SettingsSuggestIcon from '@mui/icons-material/SettingsSuggest'
import StoreIcon from '@mui/icons-material/Store'
import StorefrontIcon from '@mui/icons-material/Storefront'
import SummarizeIcon from '@mui/icons-material/Summarize'
import SyncAltIcon from '@mui/icons-material/SyncAlt'
import TableViewIcon from '@mui/icons-material/TableView'
import TuneIcon from '@mui/icons-material/Tune'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import Box from '@mui/material/Box'
import Divider from '@mui/material/Divider'
import Drawer from '@mui/material/Drawer'
import Toolbar from '@mui/material/Toolbar'
import useMediaQuery from '@mui/material/useMediaQuery'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router-dom'
import useAuth from '../../hooks/useAuth'
import paths from '../../utils/paths'
import MenuItemDrawer, { MenuItem } from './RenderDrawerList'
import { Permissions, voidDelinquentPaymentPermissions } from '@/components/ProtectedComponent/permissionName'
import { isInProduction, isInProductionLike } from '@/config'
import useGlobalContext from '@/hooks/useGlobalContext'
import * as routeActions from '@/utils/routeActions'

const drawerWidth = 240

interface Props {
  open: boolean
  alwaysTemporary?: boolean
  onClose(): void
}

export default function NavigationDrawer(props: Props): JSX.Element {
  const { t } = useTranslation()
  const { authInformation } = useAuth()
  const { hasPermission } = useGlobalContext()
  const navigate = useNavigate()
  const location = useLocation()
  const isTablet = useMediaQuery('(max-width:1199px)')
  const variant = props.alwaysTemporary ? 'temporary' : isTablet ? 'temporary' : 'permanent'
  const isIronCounty = authInformation?.jurisdictionId === 11

  const mcatUserItemList = [
    {
      text: t('dashboard'),
      icon: <DashboardIcon />,
      onClick: (): void => {
        routeActions.goToMcatHomePage(navigate)
        props.onClose()
      },
      selected: location.pathname === paths.mcatHome,
    },
    {
      text: t('telecom'),
      icon: <CellTowerIcon />,
      selected: Boolean(location.pathname.match(/^\/master\/telecom/i)),
      items: [
        {
          text: t('accounts'),
          icon: <StorefrontIcon />,
          hidden: isInProductionLike,
          onClick: (): void => {
            routeActions.goToMcatTelecomAccountsPage(navigate)
            props.onClose()
          },
          selected: Boolean(location.pathname.match(startsWithMasterTelecomAccountsRegex)),
        },
        {
          text: 'Maintenance',
          icon: <BuildIcon />,
          onClick: (): void => {
            routeActions.goToMcatTelecomMaintenancePage(navigate)
            props.onClose()
          },
          selected: location.pathname === paths.mcatTelecomMaintenancePage,
        },
      ],
    },
    {
      text: t('batch'),
      icon: <ManageAccountsIcon />,
      selected: Boolean(location.pathname.match(startsWithBatchRegex)),
      items: [
        {
          text: t('telecomBatchPenalty'),
          icon: <WarningAmberIcon />,
          onClick: (): void => {
            routeActions.goToTelecomBatchPenaltyPage(navigate)
            props.onClose()
          },
          selected: location.pathname === paths.telecomBatchPenalty,
        },
      ],
    },
  ]

  const masterUserItemList = [
    {
      text: t('dashboard'),
      icon: <DashboardIcon />,
      onClick: (): void => {
        routeActions.goToMasterHomePage(navigate)
        props.onClose()
      },
      selected: location.pathname === paths.masterHome,
    },
    {
      text: t('accountType'),
      icon: <StorefrontIcon />,
      onClick: (): void => {
        routeActions.goToMasterAccountTypesPage(navigate)
        props.onClose()
      },
      selected: location.pathname === paths.masterAccountType,
    },
    {
      text: t('basicCost'),
      icon: <SavingsIcon />,
      onClick: (): void => {
        routeActions.goToMasterBasicCostsPage(navigate)
        props.onClose()
      },
      selected: location.pathname === paths.masterBasicCost,
    },
    {
      text: t('propertyClassification'),
      icon: <SettingsApplicationsIcon />,
      onClick: (): void => {
        routeActions.goToMasterPropertyClassificationPage(navigate)
        props.onClose()
      },
      selected: location.pathname === paths.masterPropertyClassification,
    },
  ]

  const adminItemList = [
    {
      text: t('dashboard'),
      icon: <DashboardIcon />,
      onClick: (): void => {
        routeActions.goToHomePage(navigate)
        props.onClose()
      },
      selected: location.pathname === '/',
    },
    {
      text: t('accounts'),
      icon: <StorefrontIcon />,
      hidden: !hasPermission(Permissions.viewAccounts),
      onClick: (): void => {
        routeActions.goToAccounts(navigate)
        props.onClose()
      },
      selected: Boolean(location.pathname.match(startsWithAccountsRegex)),
    },
    {
      text: t('payments'),
      icon: <PaymentIcon />,
      selected: Boolean(location.pathname.match(startsWithPaymentRegex)),
      items: [
        {
          text: t('paymentSearch'),
          icon: <FindInPageIcon />,
          hidden: !hasPermission(Permissions.searchPayments),
          onClick: (): void => {
            routeActions.goToPaymentSearchPage(navigate)
            props.onClose()
          },
          selected: location.pathname === paths.paymentSearch,
        },
        {
          // Hidden if user has neither
          text: t('paymentSummary'),
          icon: <HistoryEduIcon />,
          selected: Boolean(location.pathname.match(startsWithPaymentRegex)),
          hidden:
            !hasPermission(Permissions.viewCurrentYearSummary) &&
            !hasPermission(Permissions.viewDelinquentPaymentSummary),
          items: [
            {
              text: t('currentSummary'),
              icon: <SummarizeIcon />,
              hidden: !hasPermission(Permissions.viewCurrentYearSummary),
              onClick: (): void => {
                routeActions.goToPaymentSummaryPage(navigate)
                props.onClose()
              },
              selected: location.pathname === paths.paymentSummary,
            },
            {
              text: t('delinquentSummary'),
              icon: <SummarizeIcon />,
              hidden: !hasPermission(Permissions.viewDelinquentPaymentSummary),
              onClick: (): void => {
                routeActions.goToDelinquentSummaryPage(navigate)
                props.onClose()
              },
              selected: location.pathname === paths.delinquentSummary,
            },
          ],
        },
        {
          text: t('paymentTotals'),
          icon: <AttachMoneyIcon />,
          hidden: !hasPermission(Permissions.viewDailyPaymentTotals),
          onClick: (): void => {
            routeActions.goToPaymentTotalsPage(navigate)
            props.onClose()
          },
          selected: location.pathname === paths.paymentTotals,
        },
        {
          text: t('paymentReturned'),
          icon: <HistoryEduIcon />,
          selected: Boolean(location.pathname.match(startsWithPaymentRegex)),
          hidden:
            !hasPermission([
              Permissions.viewCurrentYearReturnedCheckInformation,
              Permissions.createCurrentYearReturnedCheck,
            ]) &&
            !hasPermission([
              Permissions.viewDelinquentYearReturnedCheck,
              Permissions.saveDelinquentYearReturnedCheck,
            ]),
          items: [
            {
              text: t('currentYearReturnedPayment'),
              icon: <AssignmentReturnIcon />,
              hidden: !hasPermission([
                Permissions.viewCurrentYearReturnedCheckInformation,
                Permissions.createCurrentYearReturnedCheck,
              ]),
              onClick: (): void => {
                routeActions.goToPaymentReturnedPaymentPage(navigate)
                props.onClose()
              },
              selected: location.pathname === paths.paymentReturnedPayment,
            },
            {
              text: t('delinquentYearReturnedPayment'),
              icon: <AssignmentReturnIcon />,
              hidden: !hasPermission([
                Permissions.viewDelinquentYearReturnedCheck,
                Permissions.saveDelinquentYearReturnedCheck,
              ]),
              onClick: (): void => {
                routeActions.goToPaymentDelinquentReturnedPaymentPage(navigate)
                props.onClose()
              },
              selected: location.pathname === paths.paymentDelinquentYear,
            },
          ],
        },
        {
          text: t('refunds'),
          icon: <CurrencyExchangeIcon />,
          selected: Boolean(location.pathname.match(startsWithRefundsRegex)),
          hidden: !hasPermission(Permissions.searchRefunds) && !hasPermission(Permissions.viewRefundBatch),
          items: [
            {
              text: t('batches'),
              icon: <LayersIcon />,
              hidden: !hasPermission(Permissions.viewRefundBatch),
              onClick: (): void => {
                routeActions.goToRefundsBatchSearchPage(navigate)
                props.onClose()
              },
              selected: location.pathname === paths.refundsBatchSearch,
            },
            {
              text: t('pendingRefunds'),
              icon: <PendingActionsIcon />,
              hidden: !hasPermission(Permissions.searchRefunds),
              onClick: (): void => {
                routeActions.goToRefundsSearchPage(navigate)
                props.onClose()
              },
              selected: location.pathname === paths.refundsSearch,
            },
          ],
        },
        {
          text: t('delinquentTransactions'),
          icon: <RunningWithErrorsIcon />,
          selected: Boolean(location.pathname.match(startsWithDelinquencyRegex)),
          hidden:
            !hasPermission(voidDelinquentPaymentPermissions) &&
            !hasPermission(Permissions.viewDelinquentAdjustment),
          items: [
            {
              text: t('voidDelinquentPayment'),
              icon: <MoneyOffIcon />,
              hidden: !hasPermission(voidDelinquentPaymentPermissions),
              onClick: (): void => {
                routeActions.goToVoidDelinquentPaymentPage(navigate)
                props.onClose()
              },
              selected: location.pathname === paths.voidDelinquentPayment,
            },
            {
              text: t('delinquentAdjustment'),
              icon: <TuneIcon />,
              hidden: !hasPermission(Permissions.viewDelinquentAdjustment),
              onClick: (): void => {
                routeActions.goToDelinquentAdjustmentPage(navigate)
                props.onClose()
              },
              selected: location.pathname === paths.delinquentAdjustment,
            },
          ],
        },
        {
          text: t('massPayments'),
          icon: <AutoAwesomeMotionIcon />,
          onClick: (): void => {
            routeActions.goToMassPaymentsPage(navigate)
            props.onClose()
          },
          selected: Boolean(location.pathname.match(paths.massPayments)),
        },
      ],
    },
    {
      text: t('leaseMaster'),
      icon: <StoreIcon />,
      selected: Boolean(location.pathname.match(/^\/lease-master/i)),
      items: [
        {
          text: t('accounts'),
          icon: <AccountBalanceWalletIcon />,
          onClick: (): void => {
            routeActions.goToLeaseMasterAccountsPage(navigate)
            props.onClose()
          },
          selected: Boolean(location.pathname.match(startsWithLeaseMasterAccountsRegex)),
        },
        {
          text: t('paymentPosting'),
          icon: <ReceiptLongIcon />,
          onClick: (): void => {
            routeActions.goToLeaseMasterPaymentPostingPage(navigate)
            props.onClose()
          },
          selected: location.pathname === paths.leaseMasterPaymentPostingPage,
        },
        {
          text: t('paymentReconciliation'),
          icon: <SyncAltIcon />,
          onClick: (): void => {
            routeActions.goToLeaseMasterPaymentReconciliationPage(navigate)
            props.onClose()
          },
          selected: location.pathname === paths.leaseMasterPaymentReconciliationPage,
        },
        {
          text: t('leasedAssetReconciliation'),
          icon: <SyncAltIcon />,
          onClick: (): void => {
            routeActions.goToLeaseMasterAssetReconciliationPage(navigate)
            props.onClose()
          },
          selected: location.pathname === paths.leaseMasterAssetReconciliation,
        },
      ],
    },
    {
      text: t('telecom'),
      icon: <CellTowerIcon />,
      selected: Boolean(location.pathname.match(/^\/telecom/i)),
      hidden: !authInformation?.isTelecomUser,
      items: [
        {
          text: t('accounts'),
          icon: <AccountBalanceWalletIcon />,
          onClick: (): void => {
            routeActions.goToTelecomAccountsPage(navigate)
            props.onClose()
          },
          selected: Boolean(location.pathname.match(startsWithTelecomAccountsRegex)),
        },
        {
          text: t('paymentPosting'),
          icon: <ReceiptLongIcon />,
          onClick: (): void => {
            routeActions.goToTelecomPaymentPostingPage(navigate)
            props.onClose()
          },
          selected: location.pathname === paths.telecomPaymentPostingPage,
        },
        {
          text: t('paymentReconciliation'),
          icon: <SyncAltIcon />,
          onClick: (): void => {
            routeActions.goToTelecomPaymentReconciliationPage(navigate)
            props.onClose()
          },
          selected: location.pathname === paths.telecomPaymentReconciliationPage,
        },
      ],
    },
    {
      text: t('batch'),
      icon: <ManageAccountsIcon />,
      selected: Boolean(location.pathname.match(startsWithBatchRegex)),
      items: [
        {
          text: t('massAccount'),
          icon: <GroupsIcon />,
          hidden: isIronCounty,
          onClick: (): void => {
            routeActions.goToMassAccounts(navigate)
            props.onClose()
          },
          selected: location.pathname === paths.massAccounts,
        },
        {
          text: t('businessBatchPenalty'),
          icon: <WarningAmberIcon />,
          onClick: (): void => {
            routeActions.goToBusinessBatchPenaltyPage(navigate)
            props.onClose()
          },
          selected: location.pathname === paths.businessBatchPenalty,
        },
        {
          text: t('leaseBatchPenalty'),
          icon: <WarningAmberIcon />,
          onClick: (): void => {
            routeActions.goToLeaseBatchPenaltyPage(navigate)
            props.onClose()
          },
          selected: location.pathname === paths.leaseBatchPenalty,
        },
      ],
    },
    {
      text: t('tylerIntegration'),
      icon: <IntegrationInstructionsIcon />,
      hidden: !isIronCounty,
      selected: Boolean(location.pathname.match(startsWithTylerIntegrationRegex)),
      items: [
        {
          text: t('massAccount'),
          icon: <GroupsIcon />,
          onClick: (): void => {
            routeActions.goToTylerIntegrationMassAccounts(navigate)
            props.onClose()
          },
          selected: location.pathname === paths.tylerIntegrationMassAccounts,
        },
        {
          text: t('reports'),
          icon: <AssessmentIcon />,
          items: [
            {
              text: t('ironCountyTaxableValueReport'),
              icon: <ArticleIcon />,
              onClick: (): void => {
                routeActions.goToIronCountyTaxableValueReportsPage(navigate)
                props.onClose()
              },
              selected: location.pathname === paths.ironCountyTaxableValueReport,
            },
          ],
        },
      ],
    },
    {
      text: t('email'),
      icon: <MailIcon />,
      selected: Boolean(location.pathname.match(/^\/email/i)),
      items: [
        {
          text: t('batches'),
          icon: <AllInboxIcon />,
          selected: Boolean(location.pathname.match(/^\/email\/batch/i)),
          onClick: (): void => {
            routeActions.goToEmailBatchPage(navigate)
          },
        },
        {
          text: t('configuration'),
          icon: <SettingsIcon />,
          selected: location.pathname === paths.emailConfiguration,
          onClick: (): void => {
            routeActions.goToEmailConfigurationPage(navigate)
          },
        },
      ],
    },
    {
      text: t('admin'),
      icon: <SettingsSuggestIcon />,
      items: [
        {
          text: t('basicCost'),
          icon: <MonetizationOnIcon />,
          hidden: !hasPermission(Permissions.basicCost),
          onClick: (): void => {
            routeActions.goToBasicCostPage(navigate)
            props.onClose()
          },
          selected: location.pathname === paths.basicCost,
        },
        {
          text: t('accountType'),
          icon: <StorefrontIcon />,
          hidden: !hasPermission(Permissions.accountType),
          onClick: (): void => {
            routeActions.goToAccountTypes(navigate)
            props.onClose()
          },
          selected: location.pathname === paths.accountType,
        },
        {
          text: t('propertyClassification'),
          icon: <SettingsApplicationsIcon />,
          hidden: !hasPermission(Permissions.propertyClassification),
          onClick: (): void => {
            routeActions.goToPropertyClassificationPage(navigate)
            props.onClose()
          },
          selected: location.pathname === paths.propertyClassification,
        },
        {
          text: t('lookupTableMaintenance'),
          icon: <TableViewIcon />,
          onClick: (): void => {
            routeActions.goToLookupTableMaintenancePage(navigate)
            props.onClose()
          },
          selected: Boolean(location.pathname.match(startsWithLookupTableRegex)),
        },
        {
          text: t('sicCodes'),
          icon: <FormatListNumberedIcon />,
          items: [
            {
              text: t('sicCodeMaster'),
              icon: <SettingsApplicationsIcon />,
              onClick: (): void => {
                routeActions.goToSicCodeMatersPage(navigate)
                props.onClose()
              },
              selected: location.pathname === paths.sicCodeMasters,
            },
            {
              text: t('sicCode'),
              icon: <SettingsApplicationsIcon />,
              onClick: (): void => {
                routeActions.goToSicCodesPage(navigate)
                props.onClose()
              },
              selected: location.pathname === paths.sicCodes,
            },
            {
              text: t('sicSiloCode'),
              icon: <SettingsApplicationsIcon />,
              onClick: (): void => {
                routeActions.goToSicSiloCodesPage(navigate)
                props.onClose()
              },
              selected: location.pathname === paths.sicSiloCodes,
            },
            {
              text: t('subSiloCode'),
              icon: <SettingsApplicationsIcon />,
              onClick: (): void => {
                routeActions.goToSubSiloCodesPage(navigate)
                props.onClose()
              },
              selected: location.pathname === paths.subSiloCodes,
            },
          ],
        },
        {
          text: t('adminUsers'),
          icon: <PersonIcon />,
          onClick: () => routeActions.goToUsersPage(navigate),
          selected: location.pathname === paths.adminUsers,
        },
        {
          text: t('roles'),
          icon: <SecurityIcon />,
          onClick: () => routeActions.goToRolesPage(navigate),
          selected: location.pathname === paths.roles,
        },
        {
          text: t('countySettings'),
          icon: <SettingsSuggestIcon />,
          hidden: !hasPermission(Permissions.countySettings),
          onClick: () => routeActions.goToCountySettingsPage(navigate),
          selected: location.pathname === paths.countySettings,
        },
      ],
    },
    {
      text: t('reportModule'),
      icon: <DescriptionIcon />,
      selected: Boolean(location.pathname.match(/report-module/)),
      items: [
        {
          text: t('movePermit'),
          icon: <LocalShippingIcon />,
          onClick: (): void => {
            routeActions.goToMovePermitsPage(navigate)
            props.onClose()
          },
          selected: location.pathname === paths.movePermits,
          hidden: isInProduction || !hasPermission(Permissions.searchMovePermits),
        },
        {
          text: t('accountReports'),
          icon: <AssessmentIcon />,
          onClick: (): void => {
            routeActions.goToAccountingReportsPage(navigate)
            props.onClose()
          },
          selected: location.pathname === paths.accountReports,
        },
        {
          text: t('appraisalRegionReports'),
          icon: <MapIcon />,
          onClick: (): void => {
            routeActions.goToAppraisalRegionReportsPage(navigate)
            props.onClose()
          },
          selected: location.pathname === paths.appraisalRegionReports,
        },
        {
          text: t('letterAndForms'),
          icon: <MailIcon />,
          onClick: (): void => {
            routeActions.goToLetterAndFormReportsPage(navigate)
            props.onClose()
          },
          selected: location.pathname === paths.letterAndFormReports,
        },
        {
          text: t('notificationFiles'),
          icon: <NotificationsIcon />,
          selected: location.pathname === paths.notificationFileReports,
          onClick: (): void => {
            routeActions.goToNotificationFileReportsPage(navigate)
            props.onClose()
          },
        },
        {
          text: t('paymentReports'),
          icon: <PaidIcon />,
          onClick: (): void => {
            routeActions.goToPaymentReportsPage(navigate)
            props.onClose()
          },
          selected: location.pathname === paths.paymentReports,
        },
        {
          text: t('statusReports'),
          icon: <FactCheckIcon />,
          onClick: (): void => {
            routeActions.goToStatusReportsPage(navigate)
            props.onClose()
          },
          selected: location.pathname === paths.statusReport,
        },
        {
          text: t('taxAreaListReport'),
          icon: <MapIcon />,
          onClick: (): void => {
            routeActions.goToTaxAreaListPage(navigate)
            props.onClose()
          },
          selected: location.pathname === paths.taxAreaList,
          hidden: !hasPermission(Permissions.reportTaxAreas),
        },
        {
          text: t('leaseMasterReports'),
          icon: <StoreIcon />,
          onClick: (): void => {
            routeActions.goToLeaseMasterReportsPage(navigate)
            props.onClose()
          },
          selected: location.pathname === paths.leaseMasterReports,
        },
        {
          text: t('yearEndReports'),
          icon: <EventNoteIcon />,
          onClick: (): void => {
            routeActions.goToEndOfYearReportsPage(navigate)
            props.onClose()
          },
          selected: location.pathname === paths.endOfYearReports,
          hidden: !hasPermission(Permissions.eoyCheckListReport),
        },
      ],
    },
  ]

  const getMenuItems = (): MenuItem[] => {
    if (authInformation.isMcatUser) {
      return mcatUserItemList
    }

    if (authInformation.isMasterUser) {
      return masterUserItemList
    }

    return adminItemList
  }

  const drawer = (
    <Box sx={{ overflow: 'auto' }}>
      <MenuItemDrawer menuItems={getMenuItems()} />
      <Divider />
    </Box>
  )

  return (
    <Drawer
      variant={variant}
      open={props.open}
      onClose={props.onClose}
      ModalProps={{
        keepMounted: true,
      }}
      sx={{
        width: drawerWidth,
        ['& .MuiDrawer-paper']: { boxSizing: 'border-box', width: drawerWidth },
      }}
    >
      <Toolbar />
      {drawer}
    </Drawer>
  )
}

const startsWithAccountsRegex = new RegExp(`^${paths.accounts}`, 'i')
const startsWithPaymentRegex = new RegExp(`^${paths.payments}`, 'i')
const startsWithDelinquencyRegex = new RegExp(`^${paths.delinquency}`, 'i')
const startsWithRefundsRegex = new RegExp(`^${paths.refunds}`, 'i')
const startsWithLeaseMasterAccountsRegex = new RegExp(`^${paths.leaseMasterAccountsPage}`, 'i')
const startsWithTelecomAccountsRegex = new RegExp(`^${paths.telecomAccountsPage}`, 'i')
const startsWithMasterTelecomAccountsRegex = new RegExp(`^${paths.mcatTelecomAccountsPage}`, 'i')
const startsWithBatchRegex = new RegExp(`^${paths.batch}`, 'i')
const startsWithTylerIntegrationRegex = new RegExp(`^${paths.tylerIntegration}`, 'i')
const startsWithLookupTableRegex = new RegExp(`^${paths.lookupTableMaintenance}`, 'i')
